import type { ReactNode } from "react";

// Line icons from the CHA design canvas (24×24, round caps). Decorative: always aria-hidden.

function Icon({ size = 16, stroke = "currentColor", width = 2, children }: { size?: number; stroke?: string; width?: number; children: ReactNode }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth={width} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {children}
    </svg>
  );
}

type P = { size?: number; stroke?: string; width?: number };

export const Back = (p: P) => (
  <Icon {...p}>
    <path d="M15 18l-6-6 6-6" />
  </Icon>
);
export const History = (p: P) => (
  <Icon {...p}>
    <path d="M3 12a9 9 0 1 0 3-6.7" />
    <path d="M3 3v6h6" />
  </Icon>
);
export const Restart = (p: P) => (
  <Icon {...p}>
    <path d="M21 12a9 9 0 1 1-3-6.7" />
    <path d="M21 3v6h-6" />
  </Icon>
);
export const Check = (p: P) => (
  <Icon {...p}>
    <path d="M20 6L9 17l-5-5" />
  </Icon>
);
export const Alert = (p: P) => (
  <Icon {...p}>
    <circle cx="12" cy="12" r="10" />
    <path d="M12 8v5" />
    <path d="M12 16h.01" />
  </Icon>
);
export const Eye = (p: P) => (
  <Icon {...p}>
    <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12z" />
    <circle cx="12" cy="12" r="3" />
  </Icon>
);
export const Pencil = (p: P) => (
  <Icon {...p}>
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
  </Icon>
);
export const Play = (p: P) => (
  <Icon {...p}>
    <polygon points="6 4 20 12 6 20 6 4" />
  </Icon>
);
export const Clock = (p: P) => (
  <Icon {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </Icon>
);
export const Hand = (p: P) => (
  <Icon {...p}>
    <path d="M9 11V5a2 2 0 0 1 4 0v6" />
    <path d="M13 9a2 2 0 0 1 4 0v3" />
    <path d="M17 11a2 2 0 0 1 4 0v4a7 7 0 0 1-7 7h-1a7 7 0 0 1-6-3.4L4 15a2 2 0 0 1 3.4-2l1.6 2V7" />
  </Icon>
);
export const Key = (p: P) => (
  <Icon {...p}>
    <rect x="2" y="6" width="20" height="12" rx="2" />
    <path d="M6 10h.01M10 10h.01M14 10h.01M18 10h.01M8 14h8" />
  </Icon>
);
export const Frame = (p: P) => (
  <Icon {...p}>
    <path d="M21 12a9 9 0 1 1-9-9" />
    <path d="M21 3l-9 9" />
  </Icon>
);
export const Close = (p: P) => (
  <Icon {...p}>
    <path d="M18 6L6 18M6 6l12 12" />
  </Icon>
);
export const Book = (p: P) => (
  <Icon {...p}>
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20V3H6.5A2.5 2.5 0 0 0 4 5.5v14z" />
    <path d="M4 19.5A2.5 2.5 0 0 0 6.5 22H20v-5" />
  </Icon>
);
export const Arrow = (p: P) => (
  <Icon {...p}>
    <path d="M5 12h14" />
    <path d="M13 6l6 6-6 6" />
  </Icon>
);
// Top-bar icons for Large text, where the buttons show only an icon (decision 67).
export const Hammer = (p: P) => (
  <Icon {...p}>
    <path d="M14.5 5.5l4 4" />
    <path d="M12 8l-7 7 3 3 7-7" />
    <path d="M14 4l6 6-2 2-6-6z" />
  </Icon>
);
export const MapIcon = (p: P) => (
  <Icon {...p}>
    <path d="M9 4L3 6v14l6-2 6 2 6-2V4l-6 2z" />
    <path d="M9 4v14M15 6v14" />
  </Icon>
);
export const People = (p: P) => (
  <Icon {...p}>
    <circle cx="9" cy="8" r="3.5" />
    <path d="M2.5 20c.6-3.4 3.2-5.5 6.5-5.5s5.9 2.1 6.5 5.5" />
    <path d="M16 4.6a3.5 3.5 0 0 1 0 6.8M18.5 14.8c1.6.8 2.5 2.7 2.8 5.2" />
  </Icon>
);
// Narration (Narrate.tsx): a speaker, with sound waves while it plays.
export const Speaker = ({ waves = true, ...p }: P & { waves?: boolean }) => (
  <Icon {...p}>
    <path d="M4 9h4l5-4v14l-5-4H4z" />
    {waves && <path d="M16.5 8.5a5 5 0 0 1 0 7M19.5 5.5a9 9 0 0 1 0 13" />}
  </Icon>
);
export const TextSize = (p: P) => (
  <Icon {...p}>
    <path d="M3 19l5-13 5 13M4.8 14.5h6.4" />
    <path d="M16 19l2.5-7 2.5 7M16.9 16.8h3.2" />
  </Icon>
);
